import { useEffect } from 'react';
import { site } from '../data/site';
type SeoProps = {
  title: string;
  description: string;
  path: string;
  image?: string;
};
function upsertMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([key, value]) => el!.setAttribute(key, value));
}
function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
export function Seo({
  title,
  description,
  path,
  image
}: SeoProps) {
  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${origin}${path}`;
    const ogImage = image ?? site.logo;
    document.documentElement.lang = 'fr';
    document.title = title;
    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: description
    });
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: 'index, follow, max-image-preview:large'
    });
    upsertMeta('meta[name="viewport"]', {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1'
    });
    upsertMeta('meta[name="author"]', {
      name: 'author',
      content: site.name
    });
    upsertMeta('meta[name="geo.region"]', {
      name: 'geo.region',
      content: 'FR-PAC'
    });
    upsertMeta('meta[name="geo.placename"]', {
      name: 'geo.placename',
      content: 'Brignoles'
    });
    upsertMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: title
    });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: description
    });
    upsertMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: 'website'
    });
    upsertMeta('meta[property="og:locale"]', {
      property: 'og:locale',
      content: 'fr_FR'
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: 'og:site_name',
      content: site.name
    });
    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: url
    });
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: ogImage
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image'
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: title
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: description
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: 'twitter:image',
      content: ogImage
    });
    upsertLink('canonical', url);
    const ldId = 'ld-json-localbusiness';
    let script = document.getElementById(ldId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = ldId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: site.name,
      description,
      image: site.logo,
      logo: site.logo,
      url: origin || site.website,
      sameAs: [site.website, site.instagram],
      telephone: '+33494697428',
      email: site.email,
      foundingDate: '1978',
      address: {
        '@type': 'PostalAddress',
        streetAddress: site.address.street,
        addressLocality: site.address.city,
        addressRegion: site.address.region,
        postalCode: site.address.postalCode,
        addressCountry: 'FR'
      },
      areaServed: 'France',
      openingHoursSpecification: [{
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00'
      }],
      knowsAbout: ['rails à rideaux', 'rail à rideau électrique', 'rail rideau motorisé', 'rail à rideau professionnel', 'rail à rideau sur mesure']
    });
  }, [title, description, path, image]);
  return null;
}